import React, { useEffect, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Input,
  InputNumber,
  Select,
  Tag,
  Modal,
  Form,
  message,
  Typography,
  Row,
  Col,
  Descriptions,
  Spin,
  Divider,
  Tabs,
} from 'antd';
import {
  SearchOutlined,
  EyeOutlined,
  EditOutlined,
  UserDeleteOutlined,
  UserAddOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { adminService, CreateUserPayload, User, UserDetail } from '../../services/adminService';
import { adminWalletService, type WalletTransaction } from '../../services/walletService';
import { handleApiError } from '../../services/api';
import InactiveCustomers from './InactiveCustomers';
import GuestCustomers from './GuestCustomers';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;

// The wallet, customer-details and trader-information modals are all opened from
// buttons inside the User Details modal, but they are rendered as its siblings
// rather than its children. antd only raises a modal above another when the inner
// one sits inside the outer one's React tree, so as siblings all four share the
// default z-index of 1100 and whichever portal the browser created last wins —
// that is the order the modals were *first* opened in, not the order on screen.
// Open the row-level Edit action once and the detail modal thereafter renders on
// top of it, hiding the edit form behind the mask (issue #118). Pinning the three
// inner modals above the detail modal makes the stacking order match the way they
// are actually used. Controls inside them (Select, DatePicker) are offset from
// this value by antd, and antd's message/notification layer sits at 2000, so
// nothing collides.
const NESTED_MODAL_Z_INDEX = 1200;

const UserManagement: React.FC = () => {
  // Active tab is reflected in the URL (?tab=inactive|guests) for deep-linking;
  // 'users' is the default and carries no param. Every non-default tab key must
  // appear in TAB_PARAMS — `activeKey` is controlled, so a key missing here is
  // rewritten back to 'users' on click and that tab can never be opened.
  const [searchParams, setSearchParams] = useSearchParams();
  const TAB_PARAMS = ['inactive', 'guests'];
  const tabParam = searchParams.get('tab');
  const activeTab = tabParam && TAB_PARAMS.includes(tabParam) ? tabParam : 'users';
  const handleTabChange = (key: string) => {
    setSearchParams(TAB_PARAMS.includes(key) ? { tab: key } : {}, { replace: true });
  };

  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [filters, setFilters] = useState({
    search: '',
    userType: '',
  });
  
  // Modals
  const [userDetailVisible, setUserDetailVisible] = useState(false);
  const [createUserVisible, setCreateUserVisible] = useState(false);
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [traderEditVisible, setTraderEditVisible] = useState(false);
  const [profileEditVisible, setProfileEditVisible] = useState(false);
  // Row edits open from the table, where only the row is loaded; drawer edits
  // have the full UserDetail. Both funnel through this one record.
  const [profileEditUser, setProfileEditUser] = useState<Pick<User, 'UserID' | 'UserName' | 'UserEmail' | 'HandPhone'> | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserDetail | null>(null);
  const [createUserLoading, setCreateUserLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [traderEditLoading, setTraderEditLoading] = useState(false);
  const [profileEditLoading, setProfileEditLoading] = useState(false);

  // Quotations pagination
  const [userQuotations, setUserQuotations] = useState<UserDetail['recentQuotations']>([]);
  const [quotationPagination, setQuotationPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [quotationsLoading, setQuotationsLoading] = useState(false);

  // Wallet info for the selected user
  const [userWallet, setUserWallet] = useState<{ balance: number; currency: string; updatedAt: string } | null>(null);
  const [walletTxs, setWalletTxs] = useState<WalletTransaction[]>([]);
  const [walletLoading, setWalletLoading] = useState(false);
  const [adjustModalVisible, setAdjustModalVisible] = useState(false);
  const [adjustLoading, setAdjustLoading] = useState(false);

  const [form] = Form.useForm();
  const [createUserForm] = Form.useForm();
  const [traderForm] = Form.useForm();
  const [profileForm] = Form.useForm();
  const [adjustForm] = Form.useForm();

  useEffect(() => {
    loadUsers();
  }, [pagination.current, pagination.pageSize, filters]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await adminService.getUsers({
        page: pagination.current,
        limit: pagination.pageSize,
        search: filters.search || undefined,
        userType: filters.userType || undefined,
      });
      
      setUsers(response.data);
      setPagination(prev => ({
        ...prev,
        total: response.pagination.totalItems,
      }));
    } catch (error) {
      console.error('Failed to load users:', error);
      message.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleUserTypeFilter = (value: string) => {
    setFilters(prev => ({ ...prev, userType: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const reloadUserWallet = (userId: string) => {
    setWalletLoading(true);
    return adminWalletService
      .listUserTransactions(userId, 1, 20)
      .then(res => {
        setUserWallet(res.wallet);
        setWalletTxs(res.items);
      })
      .catch(err => {
        console.error('Failed to load wallet for user:', err);
      })
      .finally(() => setWalletLoading(false));
  };

  const openAdjustModal = () => {
    adjustForm.resetFields();
    setAdjustModalVisible(true);
  };

  const handleAdjustSubmit = async () => {
    if (!selectedUser) return;
    try {
      const values = await adjustForm.validateFields();
      const amount = Number(values.amount);
      if (!Number.isFinite(amount) || amount === 0) {
        message.error('Amount must be a non-zero number');
        return;
      }
      setAdjustLoading(true);
      const result = await adminWalletService.adjustWallet(
        selectedUser.UserID,
        amount,
        String(values.reason).trim()
      );
      message.success(
        `Wallet adjusted. New balance: ${result.currency} ${result.balance.toFixed(2)}`
      );
      setAdjustModalVisible(false);
      await reloadUserWallet(selectedUser.UserID);
    } catch (error: any) {
      if (!error?.errorFields) {
        message.error(handleApiError(error) || 'Failed to adjust wallet');
      }
    } finally {
      setAdjustLoading(false);
    }
  };

  const handleViewUser = async (userId: string) => {
    try {
      setLoading(true);
      const userDetail = await adminService.getUserDetails(userId);
      setSelectedUser(userDetail);
      setUserDetailVisible(true);

      // Load quotations with pagination
      setQuotationPagination(prev => ({ ...prev, current: 1 }));
      loadUserQuotations(userId, 1);

      // Load wallet info (balance + recent transactions). Fire-and-forget; failure shouldn't block the modal.
      setUserWallet(null);
      setWalletTxs([]);
      reloadUserWallet(userId);
    } catch (error) {
      console.error('Failed to load user details:', error);
      message.error('Failed to load user details');
    } finally {
      setLoading(false);
    }
  };

  const loadUserQuotations = async (userId: string, page: number) => {
    try {
      setQuotationsLoading(true);
      const response = await adminService.getUserQuotations(userId, {
        page,
        limit: quotationPagination.pageSize
      });

      setUserQuotations(response.quotations);
      setQuotationPagination(prev => ({
        ...prev,
        current: page,
        total: response.pagination.totalItems
      }));
    } catch (error) {
      console.error('Failed to load user quotations:', error);
    } finally {
      setQuotationsLoading(false);
    }
  };

  const handleStatusChange = (user: User) => {
    setSelectedUser(user as UserDetail);
    form.setFieldsValue({
      status: user.UserType === 'INACTIVE' ? 'active' : 'inactive',
      reason: '',
    });
    setStatusModalVisible(true);
  };

  const handleOpenCreateUser = () => {
    createUserForm.resetFields();
    createUserForm.setFieldsValue({
      userType: 'USER',
    });
    setCreateUserVisible(true);
  };

  const handleCreateUserSubmit = async () => {
    try {
      const values = await createUserForm.validateFields();
      setCreateUserLoading(true);

      const payload: CreateUserPayload = {
        userId: values.userId.trim(),
        userName: values.userName.trim(),
        password: values.password,
        userType: values.userType,
        userEmail: values.userEmail.trim(),
        handPhone: values.handPhone?.trim() || undefined,
        traderCode: values.traderCode?.trim() || undefined,
      };

      await adminService.createUser(payload);

      message.success(`User ${payload.userId} created successfully`);
      setCreateUserVisible(false);
      createUserForm.resetFields();
      setPagination(prev => ({ ...prev, current: 1 }));
      loadUsers();
    } catch (error) {
      if (error instanceof Error && error.message === 'Please confirm the password') {
        return;
      }

      console.error('Failed to create user:', error);
      message.error(handleApiError(error) || 'Failed to create user');
    } finally {
      setCreateUserLoading(false);
    }
  };

  const handleStatusSubmit = async () => {
    if (!selectedUser) return;

    try {
      const values = await form.validateFields();
      setStatusLoading(true);

      await adminService.updateUserStatus(
        selectedUser.UserID,
        values.status,
        values.reason
      );

      message.success(`User status updated to ${values.status}`);
      setStatusModalVisible(false);
      loadUsers();
    } catch (error) {
      console.error('Failed to update user status:', error);
      message.error('Failed to update user status');
    } finally {
      setStatusLoading(false);
    }
  };

  const openProfileEdit = (user: Pick<User, 'UserID' | 'UserName' | 'UserEmail' | 'HandPhone'>) => {
    setProfileEditUser(user);
    profileForm.setFieldsValue({
      name: user.UserName || '',
      email: user.UserEmail || '',
      phone: user.HandPhone || '',
    });
    setProfileEditVisible(true);
  };

  const handleProfileEditSubmit = async () => {
    if (!profileEditUser) return;

    try {
      const values = await profileForm.validateFields();
      setProfileEditLoading(true);

      // Send only what actually changed: the endpoint leaves absent fields
      // alone, so an untouched email is never re-validated or re-checked for
      // uniqueness. A blank phone is a real value — it clears the number.
      const updates: { name?: string; email?: string; phone?: string } = {};
      const name = (values.name || '').trim();
      const email = (values.email || '').trim();
      const phone = (values.phone || '').trim();
      if (name !== (profileEditUser.UserName || '')) updates.name = name;
      if (email.toLowerCase() !== (profileEditUser.UserEmail || '').toLowerCase()) updates.email = email;
      if (phone !== (profileEditUser.HandPhone || '')) updates.phone = phone;

      if (Object.keys(updates).length === 0) {
        message.info('Nothing changed');
        setProfileEditVisible(false);
        return;
      }

      const { message: resultMessage } = await adminService.updateUserProfile(
        profileEditUser.UserID,
        updates
      );

      message.success(resultMessage);
      setProfileEditVisible(false);
      loadUsers();

      // Keep the detail modal in step when the edit was started from it.
      if (selectedUser?.UserID === profileEditUser.UserID) {
        const updatedUser = await adminService.getUserDetails(profileEditUser.UserID);
        setSelectedUser(updatedUser);
      }
    } catch (error) {
      // validateFields rejects with { errorFields }; that is already shown inline.
      if (error && typeof error === 'object' && 'errorFields' in error) return;
      console.error('Failed to update customer details:', error);
      message.error(handleApiError(error));
    } finally {
      setProfileEditLoading(false);
    }
  };

  const handleEditTraderInfo = () => {
    if (!selectedUser?.traderInfo) return;

    traderForm.setFieldsValue({
      TraderName: selectedUser.traderInfo.TraderName,
      Address1: selectedUser.traderInfo.Address1,
      Address2: selectedUser.traderInfo.Address2,
      Address3: selectedUser.traderInfo.Address3,
      Tel: selectedUser.traderInfo.Tel,
      PriceLevel: selectedUser.traderInfo.PriceLevel,
      CreditLimit: selectedUser.traderInfo.CreditLimit,
      PaymentTerm: selectedUser.traderInfo.PaymentTerm,
    });
    setTraderEditVisible(true);
  };

  const handleTraderEditSubmit = async () => {
    if (!selectedUser) return;

    try {
      const values = await traderForm.validateFields();
      setTraderEditLoading(true);

      await adminService.updateTraderInfo(selectedUser.UserID, values);

      message.success('Trader information updated successfully');
      setTraderEditVisible(false);

      // Refresh user details
      const updatedUser = await adminService.getUserDetails(selectedUser.UserID);
      setSelectedUser(updatedUser);
    } catch (error) {
      console.error('Failed to update trader info:', error);
      message.error('Failed to update trader information');
    } finally {
      setTraderEditLoading(false);
    }
  };

  const getUserTypeColor = (userType: string) => {
    const colors: Record<string, string> = {
      'ADMIN': 'red',
      'USER': 'blue',
      'INACTIVE': 'default',
    };
    return colors[userType] || 'default';
  };

  const columns = [
    {
      title: 'User ID',
      dataIndex: 'UserID',
      key: 'UserID',
      width: 120,
      render: (text: string) => <Typography.Text strong>{text}</Typography.Text>,
    },
    {
      title: 'Name',
      dataIndex: 'UserName',
      key: 'UserName',
      width: 200,
    },
    {
      title: 'Email',
      dataIndex: 'UserEmail',
      key: 'UserEmail',
      width: 250,
      responsive: ['md'] as const,
    },
    {
      title: 'Phone',
      dataIndex: 'HandPhone',
      key: 'HandPhone',
      width: 150,
      responsive: ['lg'] as const,
    },
    {
      title: 'Type',
      dataIndex: 'UserType',
      key: 'UserType',
      width: 100,
      render: (type: string) => (
        <Tag color={getUserTypeColor(type)}>{type}</Tag>
      ),
    },
    {
      title: 'Trader Code',
      dataIndex: 'TraderCode',
      key: 'TraderCode',
      width: 120,
      responsive: ['lg'] as const,
    },
    {
      title: 'Last Login',
      dataIndex: 'LastLogInDate',
      key: 'LastLogInDate',
      width: 150,
      responsive: ['md'] as const,
      render: (date: string) => date ? dayjs(date).format('MMM D, YYYY') : 'Never',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, record: User) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={() => handleViewUser(record.UserID)}
          />
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => openProfileEdit(record)}
          />
          <Button
            icon={record.UserType === 'INACTIVE' ? <UserAddOutlined /> : <UserDeleteOutlined />}
            size="small"
            type={record.UserType === 'INACTIVE' ? 'primary' : 'default'}
            onClick={() => handleStatusChange(record)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2} style={{ marginBottom: 16 }}>User Management</Title>
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={[
          {
            key: 'users',
            label: 'Users',
            children: (
              <>
                <div style={{ textAlign: 'right', marginBottom: 16 }}>
                  <Button type="primary" icon={<UserAddOutlined />} onClick={handleOpenCreateUser}>
                    Add User
                  </Button>
                </div>
                <Card>
        <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
          <Col xs={24} sm={12} lg={8}>
            <Input.Search
              placeholder="Search users..."
              allowClear
              onSearch={handleSearch}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} lg={4}>
            <Select
              placeholder="User Type"
              allowClear
              style={{ width: '100%' }}
              onChange={handleUserTypeFilter}
            >
              <Select.Option value="ADMIN">Admin</Select.Option>
              <Select.Option value="USER">User</Select.Option>
              <Select.Option value="INACTIVE">Inactive</Select.Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} lg={4}>
            <Button onClick={loadUsers}>Refresh</Button>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={users}
          rowKey="UserID"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} users`,
            onChange: (page, pageSize) => {
              setPagination(prev => ({ ...prev, current: page, pageSize: pageSize || 20 }));
            },
          }}
          scroll={{ x: 1200 }}
        />
                </Card>
              </>
            ),
          },
          {
            key: 'inactive',
            label: 'Inactive Customers',
            children: <InactiveCustomers embedded />,
          },
          {
            key: 'guests',
            label: 'Guest Customers',
            children: <GuestCustomers embedded />,
          },
        ]}
      />

      <Modal
        title="Add User"
        open={createUserVisible}
        onOk={handleCreateUserSubmit}
        onCancel={() => setCreateUserVisible(false)}
        confirmLoading={createUserLoading}
        okText="Create User"
        width={640}
      >
        <Form form={createUserForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="userId"
                label="User ID"
                rules={[
                  { required: true, message: 'Please enter a user ID' },
                  { max: 50, message: 'User ID must be 50 characters or less' }
                ]}
              >
                <Input placeholder="e.g. webadmin1" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="userType"
                label="User Type"
                rules={[{ required: true, message: 'Please select a user type' }]}
              >
                <Select>
                  <Select.Option value="USER">User</Select.Option>
                  <Select.Option value="ADMIN">Admin</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="userName"
                label="Name"
                rules={[
                  { required: true, message: 'Please enter a name' },
                  { max: 100, message: 'Name must be 100 characters or less' }
                ]}
              >
                <Input placeholder="Full name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="userEmail"
                label="Email"
                rules={[
                  { required: true, message: 'Please enter an email address' },
                  { type: 'email', message: 'Please enter a valid email address' },
                  { max: 100, message: 'Email must be 100 characters or less' }
                ]}
              >
                <Input placeholder="user@example.com" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="password"
                label="Password"
                extra="Maximum 20 characters due to the current website password hashing logic."
                rules={[
                  { required: true, message: 'Please enter a password' },
                  { min: 6, message: 'Password must be at least 6 characters' },
                  { max: 20, message: 'Password must be 20 characters or less' }
                ]}
              >
                <Input.Password placeholder="Enter password" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="confirmPassword"
                label="Confirm Password"
                dependencies={['password']}
                rules={[
                  { required: true, message: 'Please confirm the password' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Passwords do not match'));
                    },
                  }),
                ]}
              >
                <Input.Password placeholder="Confirm password" />
              </Form.Item>
            </Col>
          </Row>

          <Divider style={{ margin: '8px 0 16px' }} />

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="handPhone"
                label="Phone"
                rules={[{ max: 30, message: 'Phone must be 30 characters or less' }]}
              >
                <Input placeholder="Optional phone number" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="traderCode"
                label="Trader Code"
                rules={[{ max: 50, message: 'Trader code must be 50 characters or less' }]}
              >
                <Input placeholder="Optional linked trader code" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* User Detail Modal */}
      <Modal
        title="User Details"
        open={userDetailVisible}
        onCancel={() => setUserDetailVisible(false)}
        footer={[
          <Button key="close" onClick={() => setUserDetailVisible(false)}>
            Close
          </Button>,
        ]}
        width={800}
      >
        {selectedUser && (
          <div>
            <Row justify="space-between" align="middle" style={{ marginBottom: '8px' }}>
              <Col>
                <Title level={4} style={{ margin: 0 }}>Account Information</Title>
              </Col>
              <Col>
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  size="small"
                  onClick={() => openProfileEdit(selectedUser)}
                >
                  Edit
                </Button>
              </Col>
            </Row>
            <Descriptions bordered column={2}>
              <Descriptions.Item label="User ID">{selectedUser.UserID}</Descriptions.Item>
              <Descriptions.Item label="Name">{selectedUser.UserName}</Descriptions.Item>
              <Descriptions.Item label="Email">{selectedUser.UserEmail}</Descriptions.Item>
              <Descriptions.Item label="Phone">{selectedUser.HandPhone}</Descriptions.Item>
              <Descriptions.Item label="Type">
                <Tag color={getUserTypeColor(selectedUser.UserType)}>{selectedUser.UserType}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Super User">{selectedUser.Super}</Descriptions.Item>
              <Descriptions.Item label="Trader Code">{selectedUser.TraderCode}</Descriptions.Item>
              <Descriptions.Item label="Date Created">
                {dayjs(selectedUser.DateEntry).format('MMM D, YYYY')}
              </Descriptions.Item>
            </Descriptions>

            {selectedUser.traderInfo && (
              <div style={{ marginTop: '16px' }}>
                <Row justify="space-between" align="middle" style={{ marginBottom: '8px' }}>
                  <Col>
                    <Title level={4} style={{ margin: 0 }}>Trader Information</Title>
                  </Col>
                  <Col>
                    <Button
                      type="primary"
                      icon={<EditOutlined />}
                      size="small"
                      onClick={handleEditTraderInfo}
                    >
                      Edit
                    </Button>
                  </Col>
                </Row>
                <Descriptions bordered column={2}>
                  <Descriptions.Item label="Trader Name">{selectedUser.traderInfo.TraderName}</Descriptions.Item>
                  <Descriptions.Item label="Price Level">{selectedUser.traderInfo.PriceLevel}</Descriptions.Item>
                  <Descriptions.Item label="Address">
                    {[selectedUser.traderInfo.Address1, selectedUser.traderInfo.Address2, selectedUser.traderInfo.Address3]
                      .filter(Boolean).join(', ')}
                  </Descriptions.Item>
                  <Descriptions.Item label="Phone">{selectedUser.traderInfo.Tel}</Descriptions.Item>
                  <Descriptions.Item label="Credit Limit">${selectedUser.traderInfo.CreditLimit}</Descriptions.Item>
                  <Descriptions.Item label="Payment Term">{selectedUser.traderInfo.PaymentTerm}</Descriptions.Item>
                </Descriptions>
              </div>
            )}

            <div style={{ marginTop: '16px' }}>
              <Title level={4}>Purchase History ({quotationPagination.total} orders)</Title>
              <Table
                size="small"
                dataSource={userQuotations}
                rowKey="RefNo"
                loading={quotationsLoading}
                pagination={{
                  current: quotationPagination.current,
                  pageSize: quotationPagination.pageSize,
                  total: quotationPagination.total,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                  onChange: (page) => {
                    if (selectedUser) {
                      loadUserQuotations(selectedUser.UserID, page);
                    }
                  },
                }}
                columns={[
                  { title: 'Ref No', dataIndex: 'RefNo', key: 'RefNo' },
                  {
                    title: 'Items', dataIndex: 'ItemsSummary', key: 'ItemsSummary', ellipsis: true,
                    render: (v: string | null) => v
                      ? <Text ellipsis={{ tooltip: v }} style={{ maxWidth: 260 }}>{v}</Text>
                      : <Text type="secondary">—</Text>,
                  },
                  { title: 'Date', dataIndex: 'DateIssued', key: 'DateIssued', render: (date: string) => dayjs(date).format('MMM D, YYYY') },
                  { title: 'Amount', dataIndex: 'TotalDueAmt', key: 'TotalDueAmt', render: (amount: number) => `$${amount.toFixed(2)}` },
                  { title: 'Status', dataIndex: 'webStatus', key: 'webStatus', render: (status: string) => <Tag>{status}</Tag> },
                ]}
              />
            </div>

            {/* Wallet — balance + transaction history */}
            <div style={{ marginTop: '16px' }}>
              <Row justify="space-between" align="middle">
                <Col>
                  <Title level={4} style={{ margin: 0 }}>
                    Wallet{userWallet ? ` — ${userWallet.currency} ${userWallet.balance.toFixed(2)}` : ''}
                  </Title>
                </Col>
                <Col>
                  <Button
                    icon={<DollarOutlined />}
                    onClick={openAdjustModal}
                    disabled={walletLoading}
                  >
                    Adjust balance
                  </Button>
                </Col>
              </Row>
              {userWallet && (
                <div style={{ color: '#888', fontSize: 12, marginBottom: 8 }}>
                  Last updated {dayjs(userWallet.updatedAt).format('MMM D, YYYY HH:mm')}
                </div>
              )}
              <Table
                size="small"
                rowKey="TransactionID"
                dataSource={walletTxs}
                loading={walletLoading}
                pagination={{ pageSize: 10 }}
                locale={{ emptyText: walletLoading ? 'Loading…' : 'No wallet transactions yet' }}
                columns={[
                  { title: 'Date', dataIndex: 'CreatedAt', key: 'CreatedAt', render: (d: string) => dayjs(d).format('MMM D, YYYY HH:mm') },
                  {
                    title: 'Type', dataIndex: 'Type', key: 'Type',
                    render: (t: WalletTransaction['Type']) => {
                      const color = t === 'topup' ? 'green' : t === 'debit' ? 'red' : t === 'refund' ? 'orange' : 'blue';
                      return <Tag color={color}>{t.toUpperCase()}</Tag>;
                    }
                  },
                  {
                    title: 'Amount', dataIndex: 'Amount', key: 'Amount', align: 'right' as const,
                    render: (a: number, r: WalletTransaction) => {
                      const sign = r.Type === 'debit' ? '-' : '+';
                      const color = r.Type === 'debit' ? '#cf1322' : '#3f8600';
                      const ccy = userWallet?.currency ?? 'SGD';
                      return <span style={{ color, fontWeight: 600 }}>{sign}{ccy} {Number(a).toFixed(2)}</span>;
                    }
                  },
                  {
                    title: 'Balance after', dataIndex: 'BalanceAfter', key: 'BalanceAfter', align: 'right' as const,
                    render: (b: number) => {
                      const ccy = userWallet?.currency ?? 'SGD';
                      return `${ccy} ${Number(b).toFixed(2)}`;
                    }
                  },
                  { title: 'Reference', dataIndex: 'RefID', key: 'RefID', render: (v: string | null) => v ?? '—' },
                  { title: 'Description', dataIndex: 'Description', key: 'Description', render: (v: string | null) => v ?? '—' }
                ]}
              />
            </div>
          </div>
        )}
      </Modal>

      {/* Wallet Adjustment Modal */}
      <Modal
        title={
          selectedUser
            ? `Adjust wallet — ${selectedUser.UserName || selectedUser.UserID}`
            : 'Adjust wallet'
        }
        open={adjustModalVisible}
        onOk={handleAdjustSubmit}
        onCancel={() => setAdjustModalVisible(false)}
        confirmLoading={adjustLoading}
        okText="Apply adjustment"
        zIndex={NESTED_MODAL_Z_INDEX}
      >
        {userWallet && (
          <p style={{ marginTop: 0 }}>
            <span style={{ color: '#888' }}>Current balance: </span>
            <strong>
              {userWallet.currency} {userWallet.balance.toFixed(2)}
            </strong>
          </p>
        )}
        <Form form={adjustForm} layout="vertical">
          <Form.Item
            name="amount"
            label="Amount"
            rules={[{ required: true, message: 'Amount is required' }]}
            extra="Positive to credit (top-up), negative to debit. Use the wallet currency."
          >
            <InputNumber
              style={{ width: '100%' }}
              step={1}
              precision={2}
              placeholder="e.g. 500 to credit, -50 to debit"
            />
          </Form.Item>
          <Form.Item
            name="reason"
            label="Reason"
            rules={[
              { required: true, message: 'Reason is required' },
              { max: 500, message: 'Reason must be 500 characters or less' },
            ]}
            extra="Goes into the wallet transaction log against your admin user."
          >
            <TextArea rows={3} placeholder="e.g. Manual top-up — 500 SGD goodwill credit" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Status Change Modal */}
      <Modal
        title="Change User Status"
        open={statusModalVisible}
        onOk={handleStatusSubmit}
        onCancel={() => setStatusModalVisible(false)}
        confirmLoading={statusLoading}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: 'Please select status' }]}
          >
            <Select>
              <Select.Option value="active">Active</Select.Option>
              <Select.Option value="inactive">Inactive</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="reason"
            label="Reason (Optional)"
          >
            <TextArea rows={3} placeholder="Reason for status change..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Customer Details Edit Modal (issue #100) */}
      <Modal
        title="Edit Customer Details"
        open={profileEditVisible}
        onOk={handleProfileEditSubmit}
        onCancel={() => setProfileEditVisible(false)}
        confirmLoading={profileEditLoading}
        okText="Save"
        width={520}
        zIndex={NESTED_MODAL_Z_INDEX}
      >
        <Form form={profileForm} layout="vertical">
          <Form.Item label="User ID">
            <Input value={profileEditUser?.UserID} disabled />
            <Text type="secondary" style={{ fontSize: 12 }}>
              The User ID is the customer's login and is what their orders are filed
              under, so it cannot be changed here. On older accounts it holds their
              original email — editing the email below changes the contact address
              only, and the old one still works for signing in.
            </Text>
          </Form.Item>

          <Form.Item
            name="name"
            label="Name"
            rules={[
              { required: true, message: 'Please enter the customer name' },
              { max: 100, message: 'Name must be 100 characters or less' },
            ]}
          >
            <Input placeholder="Customer name" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter an email' },
              { type: 'email', message: 'Please enter a valid email' },
              { max: 50, message: 'Email must be 50 characters or less' },
            ]}
          >
            <Input placeholder="customer@example.com" />
          </Form.Item>

          <Form.Item
            name="phone"
            label="Phone"
            rules={[{ max: 20, message: 'Phone must be 20 characters or less' }]}
          >
            <Input placeholder="Contact number" allowClear />
          </Form.Item>
        </Form>
      </Modal>

      {/* Trader Information Edit Modal */}
      <Modal
        title="Edit Trader Information"
        open={traderEditVisible}
        onOk={handleTraderEditSubmit}
        onCancel={() => setTraderEditVisible(false)}
        confirmLoading={traderEditLoading}
        width={700}
        zIndex={NESTED_MODAL_Z_INDEX}
      >
        <Form form={traderForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="TraderName"
                label="Company Name"
                rules={[{ required: true, message: 'Please enter company name' }]}
              >
                <Input placeholder="Company name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="Tel"
                label="Phone"
              >
                <Input placeholder="Phone number" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="Address1"
            label="Address Line 1"
          >
            <Input placeholder="Address line 1" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="Address2"
                label="Address Line 2"
              >
                <Input placeholder="Address line 2" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="Address3"
                label="Address Line 3"
              >
                <Input placeholder="Address line 3" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="PriceLevel"
                label="Price Level"
              >
                <Input placeholder="Price level" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="CreditLimit"
                label="Credit Limit"
              >
                <Input type="number" prefix="$" placeholder="0" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="PaymentTerm"
                label="Payment Terms"
              >
                <Input placeholder="e.g. 30 DAYS" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default UserManagement;
